import React, { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "./Result.css";
import { environment } from "environment/environment";

const NEWS_START = 0;
const NEWS_END = 30;
const NEWS_API_PATH = "/news-update/latest";
const RESULT_CALENDAR_API_PATH = "/news-update/result-calendar";
const RAPID_RESULT_API_PATH = "/news-update/rapid-results";
const RESULT_CALENDAR_PAGE = 1;
const RESULT_CALENDAR_LIMIT = 30;
const EARNINGS_UPDATE_END_STEP = 30;
const EARNINGS_UPDATE_MAX_END = 300;
const GENERIC_EARNINGS_TAGS = new Set([
    "business",
    "results",
    "earnings",
    "markets",
    "stocks",
    "wire news",
    "miscellaneous",
    "earnings first cut",
]);

const decodeHtmlEntities = (value = "") => {
    if (!value) return "--";

    if (typeof document === "undefined") {
        return value;
    }

    const textArea = document.createElement("textarea");
    textArea.innerHTML = value;

    return textArea.value.replace(/\s+/g, " ").trim();
};

const buildArticleUrl = (postUrl = "") => {
    if (!postUrl) return "";

    if (postUrl.startsWith("http://") || postUrl.startsWith("https://")) {
        return postUrl;
    }

    return `https://www.moneycontrol.com/${postUrl.replace(/^\/+/, "")}`;
};

const normalizeLookupValue = (value = "") =>
    decodeHtmlEntities(String(value ?? ""))
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const getNewsItemDateObject = (newsItem = {}) => {
    const epochValue = Number(
        newsItem.update_date_epoch ?? newsItem.creation_date_epoch
    );

    if (!Number.isNaN(epochValue) && epochValue > 0) {
        return new Date(epochValue * 1000);
    }

    const rawDate = newsItem.update_date ?? newsItem.creation_date ?? "";

    if (/^\d{14}$/.test(rawDate)) {
        const year = Number(rawDate.slice(0, 4));
        const month = Number(rawDate.slice(4, 6)) - 1;
        const day = Number(rawDate.slice(6, 8));
        const hour = Number(rawDate.slice(8, 10));
        const minute = Number(rawDate.slice(10, 12));
        const second = Number(rawDate.slice(12, 14));

        return new Date(year, month, day, hour, minute, second);
    }

    return null;
};

const getNewsHeadline = (newsItem = {}) =>
    decodeHtmlEntities(newsItem.heading || newsItem.headline || "--");

const getNewsTagsList = (newsItem = {}) =>
    String(newsItem.tags || "")
        .split(",")
        .map((tag) => decodeHtmlEntities(tag).trim())
        .filter(Boolean);

const formatApiDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const normalizePositiveInteger = (value, fallback) => {
    const parsedValue = Number.parseInt(String(value), 10);

    if (Number.isFinite(parsedValue) && parsedValue > 0) {
        return parsedValue;
    }

    return fallback;
};

const parseRapidResultNumericValue = (value = "") => {
    const normalizedValue = String(value ?? "")
        .replace(/,/g, "")
        .replace(/%/g, "")
        .trim();

    if (!normalizedValue || normalizedValue === "--") {
        return null;
    }

    const parsedValue = Number.parseFloat(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getRapidResultValueClass = (value = "") => {
    const parsedValue = parseRapidResultNumericValue(value);

    if (parsedValue === null || parsedValue === 0) {
        return "";
    }

    return parsedValue > 0 ? "rapid-result-positive" : "rapid-result-negative";
};

const formatRapidResultPercentage = (value = "") => {
    const normalizedValue = String(value ?? "").trim();

    if (!normalizedValue || normalizedValue === "--") {
        return "--";
    }

    return normalizedValue.endsWith("%") ? normalizedValue : `${normalizedValue}%`;
};

const formatRapidResultChange = (value = "") => {
    const formattedValue = formatRapidResultPercentage(value);

    return formattedValue === "--" ? "--" : `(${formattedValue})`;
};

const getRapidResultMetric = (rapidResultItem = {}, metricName = "") => {
    const metricRow = Array.isArray(rapidResultItem?.quarterData)
        ? rapidResultItem.quarterData.find((row) => row?.[0] === metricName)
        : null;

    if (!metricRow) {
        return {
            label: metricName || "--",
            current: "--",
            previous: "--",
            growth: "--",
        };
    }

    const [, currentValue = "--", previousValue = "--", growthValue = "--"] = metricRow;

    return {
        label: metricRow?.[0] || metricName || "--",
        current: currentValue,
        previous: previousValue,
        growth: formatRapidResultPercentage(growthValue),
    };
};

const getRapidResultHeaders = (rapidResultItem = {}) => {
    const [period = "Period", current = "Current", previous = "Previous", growth = "Growth"] =
        Array.isArray(rapidResultItem?.tableHeader) ? rapidResultItem.tableHeader : [];

    return { period, current, previous, growth };
};

const deriveEarningsUpdateCompanyName = (newsItem = {}) => {
    const introText = decodeHtmlEntities(newsItem.intro || "");

    if (introText.includes("|")) {
        return introText.split("|")[0].trim();
    }

    const relevantTags = getNewsTagsList(newsItem).filter(
        (tag) => !GENERIC_EARNINGS_TAGS.has(normalizeLookupValue(tag))
    );

    if (relevantTags.length > 0) {
        return relevantTags[relevantTags.length - 1];
    }

    const headlineText = getNewsHeadline(newsItem);
    const companyNameMatch = headlineText.match(
        /^(.*?)(?:\s+Q[1-4]\b|\s+FY\d{2}\b|\s+Consolidated\b|\s+Standalone\b|\s+March\b|\s+Net Sales\b|\s+profit\b|\s+results?\b)/i
    );

    return companyNameMatch?.[1]?.trim() || headlineText;
};

const formatEarningsUpdateDate = (newsItem = {}) => {
    const newsDate = getNewsItemDateObject(newsItem);

    if (!newsDate) {
        return "--";
    }

    return newsDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const extractEarningsUpdateBodyPrice = (newsItem = {}) => {
    const bodyText = decodeHtmlEntities(newsItem.body || "");
    const pricePatterns = [
        /\bCMP:\s*Rs\.?\s*([\d,.]+)/i,
        /\bshares(?:\s+on\s+the\s+[^.]+)?\s+closed(?:\s+nearly|\s+about)?(?:\s+[+-]?\d+(?:\.\d+)?(?:\s*%|\s*percent)?\s+(?:higher|lower|up|down))?\s+at\s+Rs\.?\s*([\d,.]+)/i,
        /\bshares\s+closed\s+at\s+Rs\.?\s*([\d,.]+)/i,
        /\bshares\s+closed\s+at\s+([\d,.]+)\b/i,
    ];

    for (const pricePattern of pricePatterns) {
        const priceMatch = bodyText.match(pricePattern);

        if (priceMatch?.[1]) {
            return priceMatch[1];
        }
    }

    return "";
};

const extractEarningsUpdateBodyChange = (newsItem = {}) => {
    const bodyText = decodeHtmlEntities(newsItem.body || "");

    const directionalPatterns = [
        /\bshares(?:\s+on\s+the\s+[^.]+)?\s+closed\s+(?:nearly\s+|about\s+)?([+-]?\d+(?:\.\d+)?)\s*(?:percent|%)\s*(higher|lower|up|down)/i,
        /\bshares\s+of\s+the\s+company\s+(rose|fell|gained|lost)\s+([+-]?\d+(?:\.\d+)?)%/i,
    ];

    for (const directionalPattern of directionalPatterns) {
        const changeMatch = bodyText.match(directionalPattern);

        if (!changeMatch) {
            continue;
        }

        if (directionalPattern.source.includes("shares\\s+of\\s+the\\s+company")) {
            const [, direction = "", changeValue = ""] = changeMatch;
            return /fell|lost/i.test(direction) ? `-${changeValue}` : changeValue;
        }

        const [, changeValue = "", direction = ""] = changeMatch;
        return /lower|down/i.test(direction) ? `-${changeValue}` : changeValue;
    }

    return "";
};

const getEarningsUpdateDisplayName = (newsItem = {}) =>
    deriveEarningsUpdateCompanyName(newsItem);

const getEarningsUpdatePrice = (newsItem = {}) =>
    extractEarningsUpdateBodyPrice(newsItem) || "--";

const getEarningsUpdateChange = (newsItem = {}) =>
    extractEarningsUpdateBodyChange(newsItem) || "";

const formatResultCalendarExchange = (exchangeValue = "") => {
    const normalizedExchangeValue = String(exchangeValue || "").trim().toUpperCase();

    if (normalizedExchangeValue === "N") {
        return "NSE";
    }

    if (normalizedExchangeValue === "B") {
        return "BSE";
    }

    return normalizedExchangeValue || "--";
};

const getEarningsUpdateEndOptions = () =>
    Array.from(
        { length: EARNINGS_UPDATE_MAX_END / EARNINGS_UPDATE_END_STEP },
        (_, index) => (index + 1) * EARNINGS_UPDATE_END_STEP
    );

export default function Result() {
    const [activeTab, setActiveTab] = useState("EARNINGS_UPDATE");
    const [newsList, setNewsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [earningsUpdateStart, setEarningsUpdateStart] = useState(String(NEWS_START));
    const [earningsUpdateEnd, setEarningsUpdateEnd] = useState(String(NEWS_END));
    const [resultCalendarList, setResultCalendarList] = useState([]);
    const [resultCalendarLoading, setResultCalendarLoading] = useState(false);
    const [resultCalendarError, setResultCalendarError] = useState("");
    const [resultCalendarStartDate, setResultCalendarStartDate] = useState(() => new Date());
    const [resultCalendarEndDate, setResultCalendarEndDate] = useState(() => new Date());
    const [rapidResultList, setRapidResultList] = useState([]);
    const [rapidResultLoading, setRapidResultLoading] = useState(false);
    const [rapidResultError, setRapidResultError] = useState("");
    const [rapidResultSubType, setRapidResultSubType] = useState("yoy");
    const [rapidResultLimit, setRapidResultLimit] = useState("20");
    const [rapidResultPage, setRapidResultPage] = useState("1");
    const formattedResultCalendarStartDate = formatApiDate(resultCalendarStartDate);
    const formattedResultCalendarEndDate = formatApiDate(resultCalendarEndDate);
    const normalizedEarningsUpdateStart = normalizePositiveInteger(earningsUpdateStart, NEWS_START);
    const normalizedEarningsUpdateEnd = normalizePositiveInteger(earningsUpdateEnd, NEWS_END);
    const earningsUpdateEndOptions = getEarningsUpdateEndOptions();
    const normalizedRapidResultLimit = normalizePositiveInteger(rapidResultLimit, 20);
    const normalizedRapidResultPage = normalizePositiveInteger(rapidResultPage, 1);
    const normalizedRapidResultSubType = rapidResultSubType.trim() || "yoy";

    useEffect(() => {
        let isMounted = true;

        if (activeTab !== "EARNINGS_UPDATE") {
            return undefined;
        }

        const fetchLatestNews = async () => {
            if (isMounted) {
                setLoading(true);
                setErrorMessage("");
            }

            try {
                const latestNewsResponse = await axios.get(
                    `${environment.stockApiBaseUrl}${NEWS_API_PATH}`,
                    {
                        params: {
                            start: normalizedEarningsUpdateStart,
                            end: normalizedEarningsUpdateEnd,
                        },
                    }
                );

                const latestNewsList = Array.isArray(latestNewsResponse?.data?.data)
                    ? latestNewsResponse.data.data
                    : [];

                if (isMounted) {
                    setNewsList(latestNewsList);
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error fetching latest news:", error);

                if (isMounted) {
                    setNewsList([]);
                    setErrorMessage("Unable to fetch latest news.");
                    setLoading(false);
                }
            }
        };

        fetchLatestNews();

        return () => {
            isMounted = false;
        };
    }, [activeTab, normalizedEarningsUpdateStart, normalizedEarningsUpdateEnd]);

    useEffect(() => {
        let isMounted = true;

        if (activeTab !== "RESULT_CALENDAR") {
            return undefined;
        }

        const fetchResultCalendar = async () => {
            if (isMounted) {
                setResultCalendarLoading(true);
                setResultCalendarError("");
            }

            try {
                const response = await axios.get(
                    `${environment.stockApiBaseUrl}${RESULT_CALENDAR_API_PATH}`,
                    {
                        params: {
                            page: RESULT_CALENDAR_PAGE,
                            startDate: formattedResultCalendarStartDate,
                            endDate: formattedResultCalendarEndDate,
                            limit: RESULT_CALENDAR_LIMIT,
                        },
                    }
                );

                if (isMounted) {
                    setResultCalendarList(
                        Array.isArray(response?.data?.data) ? response.data.data : []
                    );
                    setResultCalendarLoading(false);
                }
            } catch (error) {
                console.error("Error fetching result calendar:", error);

                if (isMounted) {
                    setResultCalendarList([]);
                    setResultCalendarError("Unable to fetch result calendar data.");
                    setResultCalendarLoading(false);
                }
            }
        };

        fetchResultCalendar();

        return () => {
            isMounted = false;
        };
    }, [activeTab, formattedResultCalendarStartDate, formattedResultCalendarEndDate]);

    useEffect(() => {
        let isMounted = true;

        if (activeTab !== "RAPID_RESULT") {
            return undefined;
        }

        const fetchRapidResults = async () => {
            if (isMounted) {
                setRapidResultLoading(true);
                setRapidResultError("");
            }

            try {
                const response = await axios.get(
                    `${environment.stockApiBaseUrl}${RAPID_RESULT_API_PATH}`,
                    {
                        params: {
                            limit: normalizedRapidResultLimit,
                            page: normalizedRapidResultPage,
                            subType: normalizedRapidResultSubType,
                        },
                    }
                );

                if (isMounted) {
                    setRapidResultList(
                        Array.isArray(response?.data?.data) ? response.data.data : []
                    );
                    setRapidResultLoading(false);
                }
            } catch (error) {
                console.error("Error fetching rapid results:", error);

                if (isMounted) {
                    setRapidResultList([]);
                    setRapidResultError("Unable to fetch rapid results.");
                    setRapidResultLoading(false);
                }
            }
        };

        fetchRapidResults();

        return () => {
            isMounted = false;
        };
    }, [
        activeTab,
        normalizedRapidResultLimit,
        normalizedRapidResultPage,
        normalizedRapidResultSubType,
    ]);

    const earningsUpdateStatusMessage = loading
        ? "Loading latest earnings update..."
        : errorMessage || "No earnings update records found.";
    const resultCalendarStatusMessage = resultCalendarLoading
        ? "Loading result calendar..."
        : resultCalendarError || "No result calendar data found.";
    const rapidResultStatusMessage = rapidResultLoading
        ? "Loading rapid results..."
        : rapidResultError || "No rapid results found.";
    const handleResultCalendarStartDateChange = (date) => {
        if (!date) return;

        setResultCalendarStartDate(date);

        if (resultCalendarEndDate && date > resultCalendarEndDate) {
            setResultCalendarEndDate(date);
        }
    };

    const handleResultCalendarEndDateChange = (date) => {
        if (!date) return;

        setResultCalendarEndDate(date);

        if (resultCalendarStartDate && date < resultCalendarStartDate) {
            setResultCalendarStartDate(date);
        }
    };

    const handleEarningsUpdateEndChange = (event) => {
        const nextEndValue = event.target.value;
        const normalizedNextEndValue = normalizePositiveInteger(nextEndValue, NEWS_END);
        const normalizedCurrentEndValue = normalizePositiveInteger(
            earningsUpdateEnd,
            NEWS_END
        );
        let nextStartValue = normalizedEarningsUpdateStart;

        setEarningsUpdateEnd(nextEndValue);

        if (normalizedNextEndValue > normalizedCurrentEndValue) {
            nextStartValue = normalizedCurrentEndValue + 1;
        } else if (normalizedNextEndValue <= NEWS_END) {
            nextStartValue = NEWS_START;
        } else {
            nextStartValue = Math.max(
                NEWS_START,
                normalizedNextEndValue - EARNINGS_UPDATE_END_STEP + 1
            );
        }

        setEarningsUpdateStart(
            String(Math.min(nextStartValue, normalizedNextEndValue))
        );
    };

    const handleRapidResultLimitChange = (event) => {
        setRapidResultLimit(event.target.value);
    };

    const handleRapidResultPageChange = (event) => {
        setRapidResultPage(event.target.value);
    };

    const handleRapidResultSubTypeChange = (event) => {
        setRapidResultSubType(event.target.value);
    };

    return (
        <div className="new-update">
            <header className="header">
                <nav className="nav-container">
                    <div className="header-flex">
                        <div className="left-nav">
                            <div className="logo-container">
                                <img
                                    src="/assets/images/noptionwave-logo-1.png"
                                    className="header-logo"
                                    alt="logo"
                                />
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            <div className="tab-container">
                <div className="tab">
                    <button
                        className={`tablinks sub-header-font ${activeTab === "EARNINGS_UPDATE" ? "active" : ""}`}
                        onClick={() => setActiveTab("EARNINGS_UPDATE")}
                    >
                        EARNINGS UPDATE
                    </button>

                    <button
                        className={`tablinks sub-header-font ${activeTab === "RESULT_CALENDAR" ? "active" : ""}`}
                        onClick={() => setActiveTab("RESULT_CALENDAR")}
                    >
                        RESULT CALENDAR
                    </button>

                    <button
                        className={`tablinks sub-header-font ${activeTab === "RAPID_RESULT" ? "active" : ""}`}
                        onClick={() => setActiveTab("RAPID_RESULT")}
                    >
                        RAPID RESULT
                    </button>
                </div>
            </div>

            <div className="result-page-body">
                {activeTab === "EARNINGS_UPDATE" && (
                    <div className="result-fullscreen-table">
                        <div className="table-container">
                            <div className="result-table-header">
                                <h2 className="result-table-heading">
                                    <span>EARNINGS UPDATE</span>
                                    <span className="earnings-update-range-controls">
                                        <span className="earnings-update-range-control">
                                            <span className="earnings-update-range-value">
                                                {normalizedEarningsUpdateStart}
                                            </span>
                                        </span>
                                        <span className="result-date-range-separator">to</span>
                                        <span className="earnings-update-range-control">
                                            <select
                                                value={earningsUpdateEnd}
                                                onChange={handleEarningsUpdateEndChange}
                                                className="earnings-update-range-select"
                                            >
                                                {earningsUpdateEndOptions.map((endOption) => (
                                                    <option key={endOption} value={endOption}>
                                                        {endOption}
                                                    </option>
                                                ))}
                                            </select>
                                        </span>
                                    </span>
                                </h2>
                            </div>

                            {newsList.length > 0 ? (
                                <div className="earnings-update-grid">
                                    {newsList.map((newsItem, index) => {
                                        const articleUrl = buildArticleUrl(newsItem.posturl);
                                        const displayName = getEarningsUpdateDisplayName(newsItem);
                                        const priceValue = getEarningsUpdatePrice(newsItem);
                                        const changeValue = getEarningsUpdateChange(newsItem);
                                        const formattedChangeValue = formatRapidResultChange(changeValue);
                                        const changeValueClass = getRapidResultValueClass(changeValue);
                                        return (
                                            <div
                                                key={newsItem.id ?? `${newsItem.creation_date}-${index}`}
                                                className="earnings-update-grid-card"
                                            >
                                                <div className="earnings-update-grid-card-meta">
                                                    <span>{formatEarningsUpdateDate(newsItem)}</span>
                                                    <span className="earnings-update-grid-card-unit">
                                                        Rs.Cr.
                                                    </span>
                                                </div>

                                                <div className="earnings-update-grid-card-top">
                                                    {articleUrl ? (
                                                        <a
                                                            className="earnings-update-grid-card-title earnings-update-grid-card-title-link"
                                                            href={articleUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {displayName}
                                                        </a>
                                                    ) : (
                                                        <span className="earnings-update-grid-card-title">
                                                            {displayName}
                                                        </span>
                                                    )}

                                                    <span className="earnings-update-grid-card-price-group">
                                                        <span className="earnings-update-grid-card-price">
                                                            {priceValue}
                                                        </span>

                                                        {formattedChangeValue !== "--" ? (
                                                            <span
                                                                className={`earnings-update-grid-card-change ${changeValueClass}`}
                                                            >
                                                                {formattedChangeValue}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                </div>

                                                <div className="earnings-update-grid-card-divider" />

                                                <div className="earnings-update-grid-card-headline">
                                                    {getNewsHeadline(newsItem)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="result-status-cell earnings-update-empty-state">
                                    {earningsUpdateStatusMessage}
                                </div>
                            )}
                        </div>

                        <div className="result-page-footer">noptionwave © 2026</div>
                    </div>
                )}

                {activeTab === "RESULT_CALENDAR" && (
                    <div className="result-fullscreen-table">
                        <div className="table-container">
                            <div className="result-table-header">
                                <h2 className="result-table-heading">
                                    <span>RESULT CALENDAR</span>
                                    <span className="result-calendar-date-range">
                                        <DatePicker
                                            selected={resultCalendarStartDate}
                                            onChange={handleResultCalendarStartDateChange}
                                            selectsStart
                                            startDate={resultCalendarStartDate}
                                            endDate={resultCalendarEndDate}
                                            dateFormat="yyyy-MM-dd"
                                            className="result-date-picker-input"
                                            calendarClassName="result-date-picker-calendar"
                                            popperClassName="result-date-picker-popper"
                                            popperPlacement="bottom-start"
                                        />
                                        <span className="result-date-range-separator">to</span>
                                        <DatePicker
                                            selected={resultCalendarEndDate}
                                            onChange={handleResultCalendarEndDateChange}
                                            selectsEnd
                                            startDate={resultCalendarStartDate}
                                            endDate={resultCalendarEndDate}
                                            minDate={resultCalendarStartDate}
                                            dateFormat="yyyy-MM-dd"
                                            className="result-date-picker-input"
                                            calendarClassName="result-date-picker-calendar"
                                            popperClassName="result-date-picker-popper"
                                            popperPlacement="bottom-start"
                                        />
                                    </span>
                                </h2>
                            </div>

                            {resultCalendarList.length > 0 ? (
                                <div className="result-calendar-grid">
                                    {resultCalendarList.map((item, index) => {
                                        const displayName =
                                            item.stockName || item.stockShortName || "--";
                                        const formattedChangeValue = formatRapidResultChange(
                                            item.change
                                        );
                                        const changeValueClass = getRapidResultValueClass(
                                            item.change
                                        );
                                        const exchangeLabel = formatResultCalendarExchange(
                                            item.exchange
                                        );

                                        return (
                                            <div
                                                key={item.scId ?? `${item.stockName}-${index}`}
                                                className="result-calendar-grid-card"
                                            >
                                                <div className="result-calendar-grid-card-meta">
                                                    <span>{item.date || "--"}</span>
                                                    <span className="result-calendar-grid-card-unit">
                                                        {exchangeLabel}
                                                    </span>
                                                </div>

                                                <div className="result-calendar-grid-card-top">
                                                    {item.stockUrl ? (
                                                        <a
                                                            className="result-calendar-grid-card-title result-calendar-grid-card-title-link"
                                                            href={item.stockUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {displayName}
                                                        </a>
                                                    ) : (
                                                        <span className="result-calendar-grid-card-title">
                                                            {displayName}
                                                        </span>
                                                    )}

                                                    <span className="result-calendar-grid-card-price-group">
                                                        <span className="result-calendar-grid-card-price">
                                                            {item.ltp || "--"}
                                                        </span>

                                                        {formattedChangeValue !== "--" ? (
                                                            <span
                                                                className={`result-calendar-grid-card-change ${changeValueClass}`}
                                                            >
                                                                {formattedChangeValue}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                </div>

                                                <div className="result-calendar-grid-card-footer">
                                                    <span className="result-calendar-grid-card-headline">
                                                        {item.resultType || "--"}
                                                    </span>

                                                    <div className="result-calendar-grid-card-links">
                                                        {item.stockUrl ? (
                                                            <a
                                                                className="result-link result-calendar-link-badge"
                                                                href={item.stockUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Stock
                                                            </a>
                                                        ) : (
                                                            <span className="result-calendar-grid-card-link-placeholder">
                                                                --
                                                            </span>
                                                        )}

                                                        {item.seeFinancial ? (
                                                            <a
                                                                className="result-link result-calendar-link-badge"
                                                                href={item.seeFinancial}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Financials
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="result-status-cell earnings-update-empty-state">
                                    {resultCalendarStatusMessage}
                                </div>
                            )}
                        </div>

                        <div className="result-page-footer">noptionwave © 2026</div>
                    </div>
                )}

                {activeTab === "RAPID_RESULT" && (
                    <div className="result-fullscreen-table">
                        <div className="table-container">
                            <div className="result-table-header">
                                <h2 className="result-table-heading">
                                    <span>RAPID RESULT</span>
                                    <span className="rapid-result-controls">
                                        <label className="rapid-result-control">
                                            <span>SUB TYPE</span>
                                            <select
                                                value={rapidResultSubType}
                                                onChange={handleRapidResultSubTypeChange}
                                                className="rapid-result-select"
                                            >
                                                <option value="yoy">YoY</option>
                                                <option value="qoq">QoQ</option>
                                            </select>
                                        </label>

                                        <label className="rapid-result-control">
                                            <span>LIMIT</span>
                                            <select
                                                value={rapidResultLimit}
                                                onChange={handleRapidResultLimitChange}
                                                className="rapid-result-select"
                                            >
                                                <option value="10">10</option>
                                                <option value="20">20</option>
                                                <option value="30">30</option>
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                            </select>
                                        </label>

                                        <label className="rapid-result-control">
                                            <span>PAGE</span>
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={rapidResultPage}
                                                onChange={handleRapidResultPageChange}
                                                className="rapid-result-input rapid-result-number-input"
                                            />
                                        </label>
                                    </span>
                                </h2>
                            </div>

                            {rapidResultList.length > 0 ? (
                                <div className="rapid-result-grid">
                                    {rapidResultList.map((item, index) => {
                                        const headers = getRapidResultHeaders(item);
                                        const revenueMetric = getRapidResultMetric(item, "Revenue");
                                        const grossProfitMetric = getRapidResultMetric(
                                            item,
                                            "Gross Profit"
                                        );
                                        const netProfitMetric = getRapidResultMetric(
                                            item,
                                            "Net Profit"
                                        );
                                        const stockName = item.stockName || "--";
                                        const rapidResultChangeClass = getRapidResultValueClass(
                                            item.changeP
                                        );

                                        return (
                                            <article
                                                className="rapid-result-card"
                                                key={item.scId ?? `${item.stockName}-${index}`}
                                            >
                                                <div className="rapid-result-card-meta">
                                                    <span className="rapid-result-card-date">
                                                        {item.date || "--"}
                                                    </span>
                                                    <span className="rapid-result-card-unit">
                                                        Rs.Cr.
                                                    </span>
                                                </div>

                                                <div className="rapid-result-card-price-row">
                                                    {item.stockUrl ? (
                                                        <a
                                                            className="rapid-result-stock-name rapid-result-stock-link"
                                                            href={item.stockUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {stockName}
                                                        </a>
                                                    ) : (
                                                        <span className="rapid-result-stock-name">
                                                            {stockName}
                                                        </span>
                                                    )}

                                                    <div className="rapid-result-price-group">
                                                        <span className="rapid-result-price">
                                                            {item.ltp || "--"}
                                                        </span>
                                                        <span
                                                            className={`rapid-result-change ${rapidResultChangeClass}`}
                                                        >
                                                            {formatRapidResultChange(item.changeP)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="rapid-result-divider" />

                                                <div className="rapid-result-metric-grid rapid-result-metric-grid-header">
                                                    <span className="rapid-result-metric-title">
                                                        {headers.period}
                                                    </span>
                                                    <span className="rapid-result-metric-title">
                                                        {headers.current}
                                                    </span>
                                                    <span className="rapid-result-metric-title">
                                                        {headers.previous}
                                                    </span>
                                                    <span className="rapid-result-metric-title">
                                                        {headers.growth}
                                                    </span>
                                                </div>

                                                <div className="rapid-result-divider rapid-result-divider-light" />

                                                {[revenueMetric, grossProfitMetric, netProfitMetric].map(
                                                    (metricItem) => (
                                                        <div
                                                            className="rapid-result-metric-grid"
                                                            key={metricItem.label}
                                                        >
                                                            <span className="rapid-result-metric-label">
                                                                {metricItem.label}
                                                            </span>
                                                            <span className="rapid-result-metric-value">
                                                                {metricItem.current}
                                                            </span>
                                                            <span className="rapid-result-metric-value">
                                                                {metricItem.previous}
                                                            </span>
                                                            <span
                                                                className={`rapid-result-metric-value rapid-result-growth-value ${getRapidResultValueClass(
                                                                    metricItem.growth
                                                                )}`}
                                                            >
                                                                {metricItem.growth}
                                                            </span>
                                                        </div>
                                                    )
                                                )}

                                            </article>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="result-status-cell rapid-result-empty-state">
                                    {rapidResultStatusMessage}
                                </div>
                            )}
                        </div>

                        <div className="result-page-footer">noptionwave © 2026</div>
                    </div>
                )}
            </div>
        </div>
    );
}
